import { useQuery } from '@apollo/client';
import { GET_PLAYBOOK_BY_GROUP_ID } from '@zaplify/graphql';
import { FC, useMemo, useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams, unstable_useBlocker as useBlocker, Location } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { ModalNavigationContext } from './playbooks-modal';
import { usePlaybookImages } from '../hooks/use-playbook-images';
import { playbookImages } from '../hooks/playbook-images';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@shadcn/ui/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@shadcn/ui/components/ui/input';
import { Textarea } from '@shadcn/ui/components/ui/textarea';

import { usePlaybookForm } from '../hooks/use-playbook-form';
import { Button } from '@shadcn/ui/components/ui/button';
import { ChannelType } from '@zaplify/channel-accounts-shared';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { cn } from '@shadcn/ui/lib/utils';
import { DirtyFieldsNavigationDialog } from '../components/dialogs/dirty-fields-navigation-dialog';

interface PlaybookFormProps {
    groupId?: string;
}

export const PlaybookForm: FC<PlaybookFormProps> = ({ groupId: propGroupId }) => {
    const navigate = useNavigate();
    const modalNavigation = useContext(ModalNavigationContext);
    const { groupId: paramGroupId } = useParams();
    const groupId = propGroupId || paramGroupId;
    const { toast } = useToast();

    const { unusedImgSrc } = usePlaybookImages();
    const { data: playbookData, loading: queryLoading } = useQuery(GET_PLAYBOOK_BY_GROUP_ID, {
        variables: { groupId },
        skip: !groupId,
    });
    const playbook = useMemo(() => playbookData?.Groups[0], [playbookData]);
    const { formSchema, savePlaybook } = usePlaybookForm(groupId, playbook?.AssistantSetting?.id);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            purpose: 'General outreach and relationship building',
            tone: 'Professional, concise and friendly',
            languages: ['English'],
            defaultChannel: ChannelType.LINKEDIN,
            context: '',
            default: false,
            imgSrc: unusedImgSrc,
        },
    });

    const [showDialog, setShowDialog] = useState(false);
    const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            form.formState.isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker.state === 'blocked') {
            setShowDialog(true);
            setPendingLocation(blocker.location);
        }
    }, [blocker]);

    useEffect(() => {
        if (playbook && !form.formState.isDirty) {
            form.reset(
                {
                    name: playbook.name ?? '',
                    purpose: playbook.AssistantSetting.purpose ?? '',
                    tone: playbook.AssistantSetting.tone ?? '',
                    languages: playbook.AssistantSetting.languages ?? [],
                    defaultChannel: playbook.AssistantSetting.defaultChannel || ChannelType.LINKEDIN,
                    context: playbook.AssistantSetting.context ?? '',
                    default: playbook.default ?? false,
                    imgSrc: playbook.imgSrc ?? unusedImgSrc,
                },
                {
                    keepDirty: false,
                    keepValues: false,
                }
            );
        }
    }, [playbook]);

    useEffect(() => {
        if (unusedImgSrc && !form.watch('imgSrc')) {
            form.setValue('imgSrc', unusedImgSrc);
        }
    }, [unusedImgSrc]);

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsSubmitting(true);
        try {
            await savePlaybook(values);
            if (modalNavigation) {
                modalNavigation.navigateBack();
            } else {
                // Fallback to contacts page if not in modal context
                navigate('/new/contacts');
            }
        } catch (error) {
            console.error('Failed to save playbook:', error);
            const formErrors = form.formState.errors;
            const errorMessages = Object.entries(formErrors)
                .map(([field, error]) => `${field}: ${error?.message}`)
                .join('\n');

            toast({
                title: 'Form Validation Failed',
                description: errorMessages || 'Please check all required fields are filled correctly',
                variant: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (data: z.infer<typeof formSchema>) => {
        setIsSubmitting(true);
        try {
            await onSubmit(data);
            // Navigation is already handled in onSubmit
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNavigateWithoutSaving = () => {
        if (blocker.state === 'blocked') {
            setShowDialog(false);
            setPendingLocation(null);
            blocker.proceed();
        }
    };

    const handleModalSave = async () => {
        if (blocker.state === 'blocked') {
            try {
                await form.handleSubmit(onSubmit)();
                setShowDialog(false);
                setPendingLocation(null);
                blocker.proceed();
            } catch (error) {
                console.error('Error saving:', error);
            }
        }
    };

    if (queryLoading) {
        return (
            <div className="flex justify-center items-center p-4">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    const isEditing = Boolean(groupId && playbook);

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-xl font-semibold">{isEditing ? 'Edit Playbook' : 'Create Playbook'}</h1>
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <Form {...form}>
                    {/* Playbook Name */}
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-sm font-medium">Playbook Name</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder="Enter playbook name" className="mt-1" autoFocus />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    {/* Playbook Color */}
                    <FormField
                        control={form.control}
                        name="imgSrc"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-sm font-medium">Playbook Color</FormLabel>
                                <FormControl>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {playbookImages.map((imageSrc) => (
                                            <button
                                                key={imageSrc}
                                                type="button"
                                                onClick={() => field.onChange(imageSrc)}
                                                className={cn(
                                                    'w-10 h-10 rounded-full border-2 transition-all',
                                                    field.value === imageSrc
                                                        ? 'border-primary ring-2 ring-primary/20'
                                                        : 'border-border hover:border-primary/50'
                                                )}
                                            >
                                                <img
                                                    src={imageSrc}
                                                    alt=""
                                                    className="w-full h-full rounded-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    {/* Description */}
                    <FormField
                        control={form.control}
                        name="context"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-sm font-medium">Description</FormLabel>
                                <FormControl>
                                    <Textarea
                                        {...field}
                                        placeholder="Describe how the AI should treat contacts with this playbook..."
                                        className="mt-1 min-h-[120px] resize-none"
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    {/* Hidden fields to preserve advanced settings */}
                    <div style={{ display: 'none' }}>
                        <FormField control={form.control} name="purpose" render={({ field }) => <input {...field} />} />
                        <FormField control={form.control} name="tone" render={({ field }) => <input {...field} />} />
                        <FormField
                            control={form.control}
                            name="languages"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    value={field.value.join(',')}
                                    onChange={(e) => field.onChange(e.target.value.split(',').filter(Boolean))}
                                />
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="defaultChannel"
                            render={({ field }) => <input {...field} />}
                        />
                        <FormField
                            control={form.control}
                            name="default"
                            render={({ field }) => (
                                <input
                                    type="checkbox"
                                    name={field.name}
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                />
                            )}
                        />
                    </div>

                    {/* Form Actions */}
                    <div className="flex gap-3 pt-4">
                        <Button type="submit" disabled={isSubmitting} className="px-6">
                            {isSubmitting
                                ? isEditing
                                    ? 'Saving...'
                                    : 'Creating...'
                                : isEditing
                                ? 'Save Playbook'
                                : 'Create Playbook'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => modalNavigation?.navigateBack()}>
                            Cancel
                        </Button>
                    </div>
                </Form>
            </form>

            <DirtyFieldsNavigationDialog
                open={showDialog}
                onOpenChange={(open) => {
                    setShowDialog(open);
                    if (!open && blocker.state === 'blocked') {
                        setPendingLocation(null);
                        blocker.reset();
                    }
                }}
                onSave={handleModalSave}
                onProceed={handleNavigateWithoutSaving}
            />
        </div>
    );
};
